import React, { useEffect, useState } from 'react';

import { DialogActions, DialogContent, TextField, Typography } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import AccessibleDialog from 'components/_dialogs/AccessibleDialog';
import ColoredButton from 'components/ColoredButton';
import type { RopaReviewStartSummary } from 'config/api/ropaReviews/ropaReviews.types';
import general_messages from 'messages/general_messages';
import processing_activities_messages from 'messages/processing_activities_messages';
import validation_messages from 'messages/validation_messages';
import RopaReviewDeadlineField from 'pages/ProcessingActivities/_shared/RopaReviewDeadlineField/RopaReviewDeadlineField';
import {
  getRopaReviewDeadlineErrorMessage,
  validateRopaReviewDeadline,
} from 'pages/ProcessingActivities/_shared/validateRopaReviewDeadline';

type Props = {
  loading?: boolean;
  onClose: () => void;
  onConfirm: (payload: { deadline: string; reviewInstructions: string }) => void;
  open: boolean;
  summary?: RopaReviewStartSummary;
  summaryLoading?: boolean;
};

const StartRopaReviewModal: React.FC<Props> = ({ loading = false, onClose, onConfirm, open, summary, summaryLoading = false }) => {
  const { t } = useTranslation();
  const [deadline, setDeadline] = useState('');
  const [instructions, setInstructions] = useState('');
  const [deadlineError, setDeadlineError] = useState('');
  const [instructionsError, setInstructionsError] = useState('');
  const deadlineFieldId = 'start-ropa-review-deadline';
  const instructionsFieldId = 'start-ropa-review-instructions';

  useEffect(() => {
    if (open) {
      setDeadline('');
      setInstructions('');
      setDeadlineError('');
      setInstructionsError('');
    }
  }, [open]);

  const handleClose = () => {
    setDeadlineError('');
    setInstructionsError('');
    onClose();
  };

  const applyDeadlineValue = (nextDeadline: string) => {
    setDeadline(nextDeadline);
    if (deadlineError) {
      setDeadlineError(getRopaReviewDeadlineErrorMessage(validateRopaReviewDeadline(nextDeadline), t));
    }
  };

  const handleConfirm = () => {
    let hasError = false;
    const nextDeadlineError = getRopaReviewDeadlineErrorMessage(validateRopaReviewDeadline(deadline), t);

    if (nextDeadlineError) {
      setDeadlineError(nextDeadlineError);
      hasError = true;
    }

    if (!instructions.trim()) {
      setInstructionsError(t(validation_messages.required));
      hasError = true;
    }

    if (hasError) return;

    onConfirm({
      deadline,
      reviewInstructions: instructions.trim(),
    });
  };

  return (
    <AccessibleDialog
      fullWidth
      maxWidth='sm'
      onClose={handleClose}
      open={open}
      title={t(processing_activities_messages.asset_ropa.modals.start_review.title)}
    >
      <DialogContent>
        <Typography color='textSecondary' gutterBottom variant='body2'>
          {t(processing_activities_messages.asset_ropa.modals.start_review.intro)}
        </Typography>
        <div>
          {summaryLoading ? (
            <Typography color='textSecondary' variant='body2'>
              {t(processing_activities_messages.asset_ropa.modals.start_review.summary_loading)}
            </Typography>
          ) : (
            <Typography color='textSecondary' variant='body2'>
              {t(processing_activities_messages.asset_ropa.modals.start_review.summary, {
                assets: summary?.eligibleAssetsCount ?? 0,
                owners: summary?.uniqueOperationalOwnersCount ?? 0,
                activities: summary?.assetProcessingActivitiesCount ?? 0,
              })}
            </Typography>
          )}
        </div>
        <RopaReviewDeadlineField
          autoFocus={!summaryLoading}
          error={deadlineError}
          id={deadlineFieldId}
          label={t(processing_activities_messages.asset_ropa.modals.start_review.deadline)}
          onChange={applyDeadlineValue}
          value={deadline}
        />
        <TextField
          error={Boolean(instructionsError)}
          fullWidth
          helperText={instructionsError || t(processing_activities_messages.asset_ropa.modals.start_review.instructions_help)}
          id={instructionsFieldId}
          InputLabelProps={{ htmlFor: instructionsFieldId }}
          inputProps={{ 'aria-label': t(processing_activities_messages.asset_ropa.modals.start_review.instructions) }}
          label={t(processing_activities_messages.asset_ropa.modals.start_review.instructions)}
          margin='normal'
          multiline
          onChange={event => {
            setInstructions(event.target.value);
            if (instructionsError) setInstructionsError('');
          }}
          rows={5}
          value={instructions}
          variant='outlined'
        />
      </DialogContent>
      <DialogActions>
        <ColoredButton
          className=''
          customColor='grey'
          disabled={loading}
          icon={false}
          onClick={handleClose}
          showLoader={false}
          textLike={false}
          variant='outlined'
        >
          {t(general_messages.cancel)}
        </ColoredButton>
        <ColoredButton
          className=''
          customColor='secondary'
          disabled={loading || summaryLoading}
          icon={false}
          onClick={handleConfirm}
          showLoader={loading}
          textLike={false}
          variant='contained'
        >
          {t(processing_activities_messages.asset_ropa.modals.start_review.confirm)}
        </ColoredButton>
      </DialogActions>
    </AccessibleDialog>
  );
};

export default StartRopaReviewModal;
